import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Admin Products - Product catalog management and moderation
 */
export const Route = createFileRoute('/_authenticated/admin/products')({
  component: AdminProducts,
});

function AdminProducts() {
  const [products] = useState<any[]>([]);
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      dashedBorder: 'rgba(220, 38, 38, 0.3)',
      tableBg: 'rgba(15, 15, 20, 0.4)',
      tableHeaderBg: 'rgba(26, 26, 46, 0.6)',
      inputBg: 'rgba(26, 26, 46, 0.6)',
      inputBorder: 'rgba(220, 38, 38, 0.3)',
      headerColor: '#ef4444'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      dashedBorder: '#e5e7eb',
      tableBg: '#ffffff',
      tableHeaderBg: '#f9fafb',
      inputBg: '#ffffff',
      inputBorder: '#e5e7eb',
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: c.headerColor,
              marginBottom: '0.5rem'
            }}>
              Product Management
            </h1>
            <p style={{ color: c.textMuted, fontSize: '1rem' }}>
              Manage marketplace products and creator submissions
            </p>
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard title="Total Products" value="0" theme={theme} />
          <StatCard title="Published" value="0" theme={theme} />
          <StatCard title="Pending Review" value="0" theme={theme} />
          <StatCard title="Suspended" value="0" theme={theme} />
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <input
            type="text"
            placeholder="Search products..."
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              border: `1px solid ${c.inputBorder}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: c.inputBg,
              color: c.text
            }}
          />
          <select style={{
            padding: '0.75rem 1rem',
            border: `1px solid ${c.inputBorder}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: c.inputBg,
            color: c.text
          }}>
            <option>All Types</option>
            <option>Pedal</option>
            <option>Sample Pack</option>
            <option>Preset</option>
          </select>
          <select style={{
            padding: '0.75rem 1rem',
            border: `1px solid ${c.inputBorder}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: c.inputBg,
            color: c.text
          }}>
            <option>All Status</option>
            <option>Published</option>
            <option>Pending</option>
            <option>Suspended</option>
          </select>
        </div>

        {/* Products Table */}
        {products.length === 0 ? (
          <div style={{
            background: c.cardBg,
            border: `2px dashed ${c.dashedBorder}`,
            borderRadius: '0.5rem',
            padding: '3rem',
            textAlign: 'center',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{
              fontSize: '1.125rem',
              color: c.textMuted,
              marginBottom: '1rem'
            }}>
              No products found
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              opacity: 0.7
            }}>
              Products will appear here as creators create them
            </p>
          </div>
        ) : (
          <div style={{
            background: c.tableBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: c.tableHeaderBg }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Creator</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Price</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Sales</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} style={{ borderTop: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.text, fontWeight: '500' }}>
                      {product.name}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{product.creator}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{product.type}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.text, fontWeight: '600' }}>
                      ${product.price}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{product.sales}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: product.status === 'published' ? '#d1fae5' :
                                       product.status === 'pending' ? '#fef3c7' : '#fee2e2',
                        color: product.status === 'published' ? '#065f46' :
                               product.status === 'pending' ? '#92400e' : '#991b1b'
                      }}>
                        {product.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: theme === 'dark' ? 'rgba(220, 38, 38, 0.2)' : '#fef2f2',
                          border: `1px solid ${c.cardBorder}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: c.headerColor
                        }}>
                          View
                        </button>
                        <button style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : 'white',
                          border: `1px solid ${c.cardBorder}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: '#dc2626'
                        }}>
                          Moderate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  theme: 'dark' | 'light';
}

function StatCard({ title, value, theme }: StatCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <p style={{ fontSize: '0.75rem', color: c.textMuted, marginBottom: '0.5rem' }}>
        {title}
      </p>
      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: c.text }}>
        {value}
      </p>
    </div>
  );
}

export default AdminProducts;
